import { redactSecrets } from '../../shared/privacy.js';
import type { ItemCategory, ItemColor, LostDate, ReportPlace } from '../../shared/report-schema.js';

export interface GuestSessionInfo {
  venue: { id: string; name: string; city: string };
  language: 'en';
  expiresAt: number;
}

export interface ReportDraft {
  venue: GuestSessionInfo['venue'];
  language: 'en';
  source: 'VOICE' | 'TEXT';
  transcript: Array<{ role: 'USER' | 'ASSISTANT'; text: string }>;
  item: {
    description: string;
    category: ItemCategory;
    color: ItemColor;
    brand: string;
    distinguishingFeatures: string;
  };
  lostDate: LostDate | null;
  dateInFuture: boolean;
  place: ReportPlace | null;
  proofDescription: string;
  contact: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    preferredChannel: 'EMAIL' | 'PHONE';
  };
  legal: { tosAccepted: boolean; privacyAccepted: boolean };
  walletPolicyRequired: boolean;
  walletPolicyShown: boolean;
  confidence: number;
  clarificationCount: number;
  extractionToolCallCount: number;
  startedAt: string;
  voiceStartedAt?: string;
  voiceDurationMs?: number;
  usedTextFallback: boolean;
  mandatoryFieldCorrections: number;
  reportId?: string;
  reportReference?: string;
  trackingUrl?: string;
  confirmationEmailState?: 'PENDING' | 'ACCEPTED' | 'FAILED' | 'PREVIEW';
}

export interface IntakeDraftExtraction {
  source: 'VOICE' | 'TEXT';
  text: string;
  assistantText?: string;
  item: ReportDraft['item'];
  lostDate: LostDate | null;
  dateInFuture: boolean;
  place: ReportPlace | null;
  walletPolicyRequired: boolean;
  confidence: number;
  clarificationCount: number;
  extractionToolCallCount: number;
}

export function createDraft(session: GuestSessionInfo): ReportDraft {
  return {
    venue: session.venue,
    language: session.language,
    source: 'TEXT',
    transcript: [],
    item: {
      description: '',
      category: 'UNKNOWN',
      color: 'UNKNOWN',
      brand: 'UNKNOWN',
      distinguishingFeatures: '',
    },
    lostDate: null,
    dateInFuture: false,
    place: {
      kind: 'VENUE',
      venueId: session.venue.id,
      name: session.venue.name,
      differentPlace: false,
    },
    proofDescription: '',
    contact: { firstName: '', lastName: '', email: '', phone: '', preferredChannel: 'EMAIL' },
    legal: { tosAccepted: false, privacyAccepted: false },
    walletPolicyRequired: false,
    walletPolicyShown: false,
    confidence: 0,
    clarificationCount: 0,
    extractionToolCallCount: 0,
    startedAt: new Date().toISOString(),
    usedTextFallback: false,
    mandatoryFieldCorrections: 0,
  };
}

export function restoreDraftForSession(
  saved: ReportDraft | null,
  session: GuestSessionInfo,
): ReportDraft {
  if (saved?.venue.id !== session.venue.id) return createDraft(session);
  return {
    ...saved,
    venue: session.venue,
    place:
      saved.place?.kind === 'VENUE' &&
      saved.place.venueId === session.venue.id &&
      !saved.place.differentPlace
        ? { ...saved.place, name: session.venue.name }
        : saved.place,
  };
}

export function applyIntakeExtraction(
  draft: ReportDraft,
  extraction: IntakeDraftExtraction,
): ReportDraft {
  const transcript: ReportDraft['transcript'] = [
    ...draft.transcript,
    { role: 'USER', text: redactSecrets(extraction.text) },
  ];
  if (extraction.assistantText) {
    transcript.push({ role: 'ASSISTANT', text: redactSecrets(extraction.assistantText) });
  }
  return {
    ...draft,
    source: extraction.source,
    transcript,
    item: extraction.item,
    lostDate: extraction.lostDate,
    dateInFuture: extraction.dateInFuture,
    place: extraction.place ?? draft.place,
    walletPolicyRequired: draft.walletPolicyRequired || extraction.walletPolicyRequired,
    confidence: extraction.confidence,
    clarificationCount: Math.min(2, extraction.clarificationCount),
    extractionToolCallCount: extraction.extractionToolCallCount,
    usedTextFallback: extraction.source === 'TEXT' || draft.usedTextFallback,
  };
}

export function isDraftReadyForSummary(draft: ReportDraft): boolean {
  return Boolean(
    draft.item.description.trim() &&
    draft.lostDate &&
    !draft.dateInFuture &&
    draft.place &&
    draft.contact.firstName.trim() &&
    draft.contact.lastName.trim() &&
    /^\S+@\S+\.\S+$/.test(draft.contact.email) &&
    (draft.contact.preferredChannel !== 'PHONE' || draft.contact.phone.trim()) &&
    draft.legal.tosAccepted &&
    draft.legal.privacyAccepted,
  );
}

export function resetForAnotherItem(draft: ReportDraft): ReportDraft {
  return createDraft({ venue: draft.venue, language: draft.language, expiresAt: 0 });
}

export function resetIntake(draft: ReportDraft): ReportDraft {
  const clean = createDraft({ venue: draft.venue, language: draft.language, expiresAt: 0 });
  const retained = { ...draft };
  delete retained.voiceStartedAt;
  delete retained.voiceDurationMs;
  return {
    ...retained,
    source: clean.source,
    transcript: clean.transcript,
    item: clean.item,
    lostDate: clean.lostDate,
    dateInFuture: clean.dateInFuture,
    place: clean.place,
    walletPolicyRequired: false,
    walletPolicyShown: false,
    confidence: 0,
    clarificationCount: 0,
    extractionToolCallCount: 0,
    usedTextFallback: false,
    mandatoryFieldCorrections: 0,
  };
}
